import { MarkerType } from '@xyflow/react';
import { enum__AppElementType, enum_ENUM_FLUJO_EP } from '../../../../common/Dictionaries';
import { __GetClientRoleCode } from '../../../../common/CommonFuctions';

export function buildFlowNodesAndEdges({ edpPrepareHeader, contextSite }) {
    const roleAEC = __GetClientRoleCode(contextSite.applicationRoles, enum__AppElementType.CONTRACT, 'AEC');
    const roleATO = __GetClientRoleCode(contextSite.applicationRoles, enum__AppElementType.CONTRACT, 'ATO');
    const roleAAD = __GetClientRoleCode(contextSite.applicationRoles, enum__AppElementType.CONTRACT, 'AAD');
    const roleADC = __GetClientRoleCode(contextSite.applicationRoles, enum__AppElementType.CONTRACT, 'ADN');
    const roleRFA = __GetClientRoleCode(contextSite.applicationRoles, enum__AppElementType.CONTRACT, 'RFA');

    const estado = edpPrepareHeader.estadO_FLUJO_ID;
    const revisionList = (edpPrepareHeader.l_etapas_revision || []).filter(x => x.revisionDinamica === true);
    const rfaCount = Math.max(edpPrepareHeader.cantidad_rfa || 0, 0);

    const iccItem = revisionList.find(x => (x.nombre || '').trim().toUpperCase().includes('ICC'));
    const lccItem = revisionList.find(x => (x.nombre || '').trim().toUpperCase().includes('LCC'));

    const nodes = [];
    const edges = [];

    const edgeStyle = {
        stroke: '#b1b1b7',
        strokeWidth: 2
    };

    const markerEnd = {
        type: MarkerType.ArrowClosed,
        color: '#b1b1b7',
        width: 25,
        height: 25
    };

    const createEdge = (source, target, extra = {}) => ({
        id: `e${source}-${target}`,
        source: String(source),
        target: String(target),
        animated: false,
        label: '',
        type: 'step',
        style: edgeStyle,
        markerEnd,
        ...extra
    });

    const createNode = ({
        id,
        label,
        x,
        y,
        width = 130,
        height = 80,
        type,
        sourcePosition,
        targetPosition,
        active = false,
        rejected = false,
        extraStyle = {}
    }) => ({
        id: String(id),
        data: { label },
        ...(type ? { type } : {}),
        ...(sourcePosition ? { sourcePosition } : {}),
        ...(targetPosition ? { targetPosition } : {}),
        position: { x, y },
        style: {
            height,
            width,
            border: '1px solid #000',
            borderRadius: '5px',
            color: active ? 'white' : '',
            backgroundColor: active ? (rejected ? 'red' : '#31a915') : '',
            ...extraStyle
        }
    });

    // -----------------------------
    // NODOS BASE
    // -----------------------------
    nodes.push(
        createNode({
            id: 1,
            label: `${roleAEC} Preparación`,
            x: 30,
            y: 10,
            active: estado === enum_ENUM_FLUJO_EP.PREPARACION,
            sourcePosition: 'right'
        }),
        createNode({
            id: 2,
            label: `${roleAEC} Rechazado`,
            x: 30,
            y: 120,
            active: estado === enum_ENUM_FLUJO_EP.RECHAZADO,
            rejected: true,
            type: 'customNode'
        }),
        createNode({
            id: 3,
            label: 'EP Anulado',
            x: 30,
            y: 230,
            active: estado === enum_ENUM_FLUJO_EP.ANULADO,
            rejected: true,
            type: 'output',
            targetPosition: 'top'
        }),
        createNode({
            id: 4,
            label: `${roleATO}/${roleAAD} Revisión`,
            x: 205,
            y: 10,
            width: 130,
            height: 300,
            active: estado === enum_ENUM_FLUJO_EP.REVISION && !iccItem?.highlight && !lccItem?.highlight,
            type: 'customNode'
        })
    );

    // -----------------------------
    // EDGES BASE
    // -----------------------------
    edges.push(
        createEdge(1, 4),
        createEdge(2, 4),
        createEdge(2, 3, { sourceHandle: 'bottomHandle' }),
        createEdge(4, 2, { sourceHandle: 'bottomHandle' })
    );

    // -----------------------------
    // CADENA HORIZONTAL:
    // ICC? -> LCC? -> ADC
    // -----------------------------
    const horizontalChain = [];

    if (iccItem) {
        horizontalChain.push({
            kind: 'ICC',
            label: iccItem.nombre || 'Revisión ICC',
            active: estado === enum_ENUM_FLUJO_EP.REVISION && iccItem.highlight === true,
            width: 130,
            height: 300,
            type: 'customNode'
        });
    }

    if (lccItem) {
        horizontalChain.push({
            kind: 'LCC',
            label: lccItem.nombre || 'Revisión LCC',
            active: estado === enum_ENUM_FLUJO_EP.REVISION && lccItem.highlight === true,
            width: 130,
            height: 300,
            type: 'customNode'
        });
    }

    horizontalChain.push({
        kind: 'ADN',
        label: `${roleADC} Aprobación`,
        active: estado === enum_ENUM_FLUJO_EP.ABROPACION_ADN,
        width: 130,
        height: 300,
        type: 'customNode'
    });

    let nextId = 5;
    let previousId = 4;
    let x = 380;

    horizontalChain.forEach((item) => {
        nodes.push(
            createNode({
                id: nextId,
                label: item.label,
                x,
                y: 10,
                width: item.width,
                height: item.height,
                type: item.type,
                sourcePosition: 'right',
                targetPosition: 'left',
                active: item.active
            })
        );

        edges.push(createEdge(previousId, nextId));

        previousId = nextId;
        nextId++;
        x += 175;
    });

    // -----------------------------
    // COLUMNA RFA
    // -----------------------------
    const rfaColumnX = x;
    let lastRfaId = null;

    for (let i = 1; i <= rfaCount; i++) {
        const enumMap = {
            1: enum_ENUM_FLUJO_EP.ABROPACION_RFA1,
            2: enum_ENUM_FLUJO_EP.ABROPACION_RFA2,
            3: enum_ENUM_FLUJO_EP.ABROPACION_RFA3,
        };

        let rfaY = 120;
        let sourcePosition = 'right';
        let targetPosition = 'left';

        if (rfaCount === 1) {
            rfaY = 120;
            sourcePosition = 'right';
            targetPosition = 'left';
        }
        else if (rfaCount === 2) {
            rfaY = i === 1 ? 120 : 230;
            sourcePosition = i === 2 ? 'right' : 'bottom';
            targetPosition = i === 1 ? 'left' : 'top';
        }
        else if (rfaCount === 3) {
            rfaY = i === 1 ? 10 : (i === 2 ? 120 : 230);
            sourcePosition = i === 3 ? 'right' : 'bottom';
            targetPosition = i === 1 ? 'left' : 'top';
        }

        const rfaId = nextId;

        nodes.push(
            createNode({
                id: rfaId,
                label: `${roleRFA}${i} Aprobación`,
                x: rfaColumnX,
                y: rfaY,
                width: 110,
                height: 80,
                active: estado === enumMap[i],
                sourcePosition,
                targetPosition
            })
        );

        if (i === 1) {
            // ADC -> RFA1
            edges.push(createEdge(previousId, rfaId));
        } else {
            // RFA anterior -> RFA actual
            edges.push(createEdge(lastRfaId, rfaId));
        }

        lastRfaId = rfaId;
        nextId++;
    }

    // -----------------------------
    // EP APROBADO
    // -----------------------------
    const approvedId = nextId;
    const approvedX = rfaCount > 0 ? (rfaColumnX + 165) : x;

    nodes.push(
        createNode({
            id: approvedId,
            label: 'EP Aprobado',
            x: approvedX,
            y: 10,
            width: 130,
            height: 300,
            type: 'output',
            targetPosition: 'left',
            active: estado === enum_ENUM_FLUJO_EP.APROBADO
        })
    );

    // Si hay RFA -> último RFA conecta a aprobado
    // Si no hay RFA -> ADC conecta a aprobado
    edges.push(createEdge(lastRfaId ?? previousId, approvedId));

    return { nodes, edges };
}